import { StorageLocationDto } from '@nest/domain-entities-metadata-generated';
import { Button, Label, Table, TableBody, TableCell, TableHead, TableRow, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { StorageLocationScanInput } from '../components/StorageLocationScanInput';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';
import { useInvalidateTerminalState } from './useTerminalState.hook';

export default function OutboundWavelessUnloadPage() {
	const documentTypeId = useNumberParam('documentTypeId');
	const queueId = useNumberParam('queueId');
	const client = useTerminalClient();
	const navigate = useNavigate();
	const invalidateTerminalState = useInvalidateTerminalState();

	const documentType = useQuery({
		queryKey: ['outbound-document-type', documentTypeId],
		queryFn: () => client.outboundDeliveryOrderDocumentTypes.getById.execute({ id: documentTypeId! }),
		enabled: !!documentTypeId
	});

	const { data: unloadOverview } = useQuery({
		queryKey: ['waveless-unload-overview'],
		queryFn: () => client.outboundDeliveryOrders.getWavelessUnloadOverviewQuery.execute({})
	});

	const [destinationLocation, setDestinationLocation] = useState<StorageLocationDto | null>(null);

	const unloadMutation = useMutation({
		mutationFn: async () =>
			client.outboundDeliveryOrders.outboundWavelessUnloadMutation.execute({
				documentTypeId: documentTypeId!,
				destionationLocationId: destinationLocation?.id!
			}),
		onSuccess: () => {
			invalidateTerminalState();
			navigate(`/outbound/${documentTypeId}/waveless/${queueId}`);
		}
	});

	useValidationResultDialog([unloadMutation.error]);

	const bottomContent = (
		<div className="mt-2 sticky bottom-0 bg-background">
			<Button className="w-full" onClick={() => unloadMutation.mutate()} disabled={unloadMutation.isPending || !destinationLocation}>
				Vyložit
			</Button>
		</div>
	);

	return (
		<TerminalScreen showBackButton topBar={documentType.data?.name} bottomContent={bottomContent}>
			<div className="p-2 flex flex-col gap-4 flex-1 overflow-auto h-full">
				{unloadOverview && (
					<>
						<Table>
							<TableBody>
								<TableRow>
									<TableHead className="w-1/2">Vychystávací paleta</TableHead>
									<TableCell className="w-1/2 font-mono font-bold">{unloadOverview.pickingHandlingUnit?.code}</TableCell>
								</TableRow>
							</TableBody>
						</Table>

						<Label className="block">Cílová lokace</Label>

						{unloadOverview.requestUnloadWarehouseSubInventory && (
							<div className="rounded-md p-3 bg-amber-100 text-amber-900">
								<div className="">Požadovaná oblast</div>
								<div className="font-semibold">{unloadOverview.requestUnloadWarehouseSubInventory.name}</div>
							</div>
						)}

						<StorageLocationScanInput placeholder="Naskenujte lokaci..." value={destinationLocation} onChange={setDestinationLocation} autoFocus />

						<Label className="mb-1 block">Naložené položky</Label>
						<div className="mt-2 space-y-2 divide-y gap-2 p-2 rounded-md border border-input-border">
							{(unloadOverview.warehouseProcessBatchItems ?? []).map(item => (
								<div key={item.id} className="w-full flex items-center gap-3">
									<div className="rounded-md bg-slate-100 px-2 py-1 text-sm font-semibold">{item.wavelessQuantityLoaded ?? 0}ks</div>
									<div className="font-semibold">{item.wavelessOutboundDeliveryOrderLine?.stockItem?.item?.name}</div>
								</div>
							))}
						</div>
					</>
				)}
			</div>
		</TerminalScreen>
	);
}
